import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import {
    createElement,
    updateElement,
    setInnerText,
} from '../../global/render-api';
import type { AttributeValue } from '../ui-element.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import styles from './ui-amount.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAmount
 * @element ui-amount
 * @classdesc Represents a class for <code>ui-amount</code> element.
 * Create element with bigger sum and additional feature to control such element,
 * like leading +/- characters, precious, format, etc.
 * @property {number} [value=0] {@attr value} Value of amount like 13.9, 134.3 etc.
 * @property {number} [accuracy=1] {@attr accuracy} Formats a number using fixed-point notation
 * (-1 to disable accuracy).
 * @property {string} [currency] {@attr currency} Currency label like USD, EUR, SEK etc.
 * @property {boolean} [leadingPlus=false] {@attr leading-plus} If true, positive value would be
 * with + at the beginning.
 * @property {boolean} [format=false] {@attr format} If true, thousands separators will be added.
 * @slot {@name additional} Additional info
 * @example
 * <ui-amount value="100.56" currency="EUR" leading-plus>
 *   <a href="#" slot="additional">65.87</a>
 *   <span slot="additional"> Reserved</span>
 * </ui-amount>
 */
@Component({
    tag: 'ui-amount',
    styles,
})
export class UIAmount extends UIElement {
    @Attribute(Number) accuracy: number = -1;
    @Attribute(String) currency?: string;
    @Attribute(Boolean) leadingPlus?: boolean;
    @Attribute(Number) value: number = 0;
    @Attribute(Boolean) format?: boolean;

    @Child('div.ui-amount__main') mainContainerElement!: HTMLDivElement;
    @Child('div.ui-amount__additional')
    additionalAmountElement?: HTMLDivElement;
    @Child('div.ui-amount__main span.ui-amount__value-positive')
    positiveValueElement?: HTMLSpanElement;
    @Child('div.ui-amount__main span.ui-amount__value-negative')
    negativeValueElement?: HTMLSpanElement;
    @Child('span.ui-amount__currency') currencyElement?: HTMLSpanElement;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): Array<string> {
        return ['accuracy', 'value', 'currency', 'leading-plus'];
    }

    /**
     * Returns the formatted value string based on current component state,
     * without any DOM side effects.
     * @private
     * @returns {string}
     */
    private getValueString(): string {
        if (!this.isCorrectValue()) {
            return '0';
        }
        let valueString = String(this.value);
        if (this.accuracy > -1) {
            valueString = Number(valueString).toFixed(this.accuracy);
        }
        if (this.value > 0 && this.leadingPlus) {
            valueString = '+' + valueString;
        }
        if (this.format) {
            valueString = valueString.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
        }
        return valueString;
    }

    /**
     * Returns the existing value span from the main container, or null if absent.
     * @private
     * @returns {HTMLSpanElement | null}
     */
    private getValueElement(): HTMLSpanElement | null {
        if (!this.mainContainerElement) {
            return null;
        }
        return (
            this.positiveValueElement ??
            this.negativeValueElement ??
            this.mainContainerElement.querySelector<HTMLSpanElement>(
                'span:not(.ui-amount__currency)'
            )
        );
    }

    /**
     * Attach slots to needed places
     * @param {Array<HTMLElement>} children
     */
    renderSlots(children: Array<HTMLElement>): void {
        if (children.length === 0) {
            return;
        }
        this.renderAdditionalAmountElement();
        children.forEach((child) =>
            this.additionalAmountElement?.appendChild(child)
        );
    }

    /**
     * Updates the value span (creates it if needed) and returns it.
     * @private
     */
    private renderValue(): void {
        let element = this.getValueElement();
        if (!element) {
            element = createElement<HTMLSpanElement>({ tagName: 'span' })!;
            this.mainContainerElement.appendChild(element);
        }
        const numericValue = this.isCorrectValue() ? this.value : 0;
        updateElement(element, {
            classList: {
                'ui-amount__value-positive': numericValue >= 0,
                'ui-amount__value-negative': numericValue < 0,
            },
        });
        setInnerText(element, this.getValueString());
    }

    /**
     * @private
     * @returns {boolean}
     */
    private isCorrectValue(): boolean {
        return this.value != null && !Number.isNaN(this.value);
    }

    /**
     * Render layout for common amount.
     */
    renderLayout(): void {
        this.renderMainContainer();
        this.renderValue();
        if (this.currency) {
            this.renderCurrency();
        }
    }

    /**
     * Updates the currency span in-place (creates it if needed).
     * @private
     */
    private renderCurrency(): void {
        let element = this.currencyElement;
        if (!element) {
            element = createElement<HTMLSpanElement>({
                tagName: 'span',
                classList: { 'ui-amount__currency': true },
            })!;
            this.mainContainerElement.appendChild(element);
        }
        setInnerText(element, ` ${this.currency}`);
    }

    /**
     * Renders main container element if it doesn't exist.
     * @private
     */
    private renderMainContainer(): void {
        if (this.mainContainerElement) {
            return;
        }
        this.appendChild(
            createElement<HTMLDivElement>({
                tagName: 'div',
                classList: { 'ui-amount__main': true },
            })!
        );
    }

    /**
     * Renders additional amount element if it doesn't exist.
     * @private
     */
    private renderAdditionalAmountElement(): void {
        if (this.additionalAmountElement) {
            return;
        }
        this.appendChild(
            createElement<HTMLDivElement>({
                tagName: 'div',
                classList: { 'ui-amount__additional': true },
            })!
        );
    }

    /**
     * Get element for main value.
     * @deprecated Prefer `renderValue` for explicit DOM updates.
     * @returns {HTMLSpanElement | null}
     */
    getValue(): NullOr<HTMLSpanElement> {
        return this.getValueElement();
    }

    /**
     * Get element for currency.
     * @deprecated Prefer `renderCurrency` for explicit DOM updates.
     * @returns {HTMLSpanElement}
     */
    getCurrency(): UndefinedOr<HTMLSpanElement> {
        return this.currencyElement;
    }

    /**
     * Get container element for additional info.
     * @deprecated Prefer `renderAdditionalAmountElement` for explicit DOM updates.
     * @returns {HTMLDivElement}
     */
    getAdditionalContainer(): UndefinedOr<HTMLDivElement> {
        return this.additionalAmountElement;
    }

    /**
     * Get container element for main value and currency.
     * @deprecated Prefer `renderMainContainer` for explicit DOM updates.
     * @returns {HTMLDivElement}
     */
    getMainContainer(): HTMLDivElement {
        return this.mainContainerElement;
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        switch (name) {
            case 'accuracy':
            case 'leading-plus':
            case 'value':
                this.renderValue();
                break;
            case 'currency':
                this.renderCurrency();
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const childrenForSlot = this.getChildrenForSlot('additional');
        this.renderLayout();
        if (childrenForSlot.length) {
            this.renderSlots(childrenForSlot);
        }
    }
}
